/**
 * Product snapshot reads (PR 10, plan I5) — the shared "how is this product
 * doing" facts behind the standard first-7 columns and the quick-view drawer.
 *
 * ONE query per scope for the whole app: every product table on screen asks
 * `useProductSnapshot(marketplace)` and gets the same cached answer (the
 * server caches the full catalog for 5 minutes too, keyed like Restock's), so
 * adding the columns to four tables costs one request, not four.
 */
import { useMemo } from 'react'
import {
  getProductQuickView, getProductSnapshot, getUnlinkedSales,
  type ProductQuickView, type ProductSnapshot, type ProductSnapshotWindow,
} from '../ops/lib/api'
import { keys } from './query/keys'
import { useApiQuery } from './query/useApiQuery'

const FIVE_MIN = 5 * 60_000
const EMPTY = new Map<string, ProductSnapshot>()

/** uid → snapshot for one scope ('all' or a hub marketplace name), plus the
 *  window the money covers. `enabled: false` skips the request (a table that
 *  is not showing the columns). */
export function useProductSnapshot(
  marketplace: string,
  opts: { enabled?: boolean } = {},
): { byUid: Map<string, ProductSnapshot>; window: ProductSnapshotWindow | null; loading: boolean } {
  const q = useApiQuery(
    keys.products.snapshot(marketplace),
    () => getProductSnapshot(marketplace),
    { enabled: opts.enabled ?? true, staleTime: FIVE_MIN },
  )
  const data = q.data
  const byUid = useMemo(
    () => (data ? new Map(data.items.map((it) => [it.uid, it])) : EMPTY),
    [data],
  )
  return { byUid, window: data?.window ?? null, loading: q.isPending && (opts.enabled ?? true) }
}

/** The quick-view drawer's one read. Disabled while no product is open. */
export function useProductQuickViewData(uid: string | null, marketplace: string) {
  return useApiQuery<ProductQuickView>(
    keys.products.quickView(uid ?? '', marketplace),
    () => getProductQuickView(uid as string, marketplace),
    { enabled: !!uid, staleTime: FIVE_MIN },
  )
}

/** Selling, not in the catalog (#96 / #97) — the Listings chip and drawer. */
export function useUnlinkedSales(days = 30) {
  return useApiQuery(keys.products.unlinkedSales(days), () => getUnlinkedSales({ days }), { staleTime: FIVE_MIN })
}
